/**
 * The guide catalogue.
 *
 * The Markdown itself is vendored from the `docs` repository rather than
 * rewritten here — one source of truth, and `scripts/sync-docs.mjs` both
 * refreshes the copies and asserts they still match what it recorded. Each
 * guide is behind a dynamic import so a reader who opens one guide downloads
 * one guide, and a session that never opens the documentation downloads none.
 */

export interface Guide {
  slug: string
  /** Console-side title. The document keeps its own heading; this names the card. */
  title: string
  audience: string
  summary: string
  /** The file this was vendored from, in the `docs` repository. */
  source: string
  load: () => Promise<string>
}

export const guides: readonly Guide[] = [
  {
    slug: 'renting',
    title: 'Renting compute',
    audience: 'For tenants',
    summary:
      'Rent a machine, put a container on it, pay for it, and take it down again — the console flow and the CLI that does the same thing.',
    source: 'TENANT-GUIDE.md',
    load: () => import('./content/tenant-guide.md?raw').then((module) => module.default),
  },
  {
    slug: 'hosting',
    title: 'Becoming a host',
    audience: 'For providers',
    summary:
      'Sell compute on a DDWS network: cluster, wallet, certificates, on-chain registration, the daemon, and the checks that prove you are live.',
    source: 'PROVIDER-ONBOARDING.md',
    load: () => import('./content/provider-onboarding.md?raw').then((module) => module.default),
  },
  {
    slug: 'nodes',
    title: 'Running a node',
    audience: 'For node operators',
    summary:
      'Join a DDWS network as a full node, then as a validator — including the consensus duties this fork adds that a vanilla Cosmos validator does not carry.',
    source: 'NODE-OPERATOR.md',
    load: () => import('./content/node-operator.md?raw').then((module) => module.default),
  },
]

export function findGuide(slug: string | undefined): Guide | undefined {
  return guides.find((guide) => guide.slug === slug)
}

/**
 * Documents that live in the `docs` repository and are not published here.
 *
 * The guides cross-reference their neighbours by relative path. Rendering those
 * as links would produce a page of 404s, so they render as named references
 * instead — the reader learns the document exists and what to ask for, and the
 * page keeps its promise that every link on it goes somewhere.
 */
export function isRepositoryReference(href: string): boolean {
  return !/^(?:https?:|mailto:|#|\/)/.test(href)
}
